import { TronWebProps } from './tron.types'
import { notification } from 'antd'
import { RCC_ABI } from './abi'
import { RCC_ADDRESS } from './tron.consts'

class TronLinkHelper {
    tronWeb?: TronWebProps
    base58Address?: string
    instanceRcc?: Record<string, any>

    private _connect = async (): Promise<boolean> => {
        // Расширение не установлено — нечего и просить.
        if (!window.tronLink) {
            return false
        }
        try {
            const dataSource = await window.tronLink.request({
                method: 'tron_requestAccounts',
            })

            // code 200 — подключено.  Любой другой код (4001 «rejected»,
            // отсутствие кода) — не подключено.  Молча возвращаем результат:
            // понятное сообщение покажет ensureConnected по клику, без шума в
            // консоли.
            return dataSource?.code === 200
        } catch {
            // Напр. -32603 «No active wallet found» — кошелёк заблокирован или
            // нет активного аккаунта.  Не фатально: initTronWeb ещё попробует
            // взять адрес из инжектированного tronWeb.
            return false
        }
    }

    /**
     * Подключение по требованию (по клику пользователя).  Если уже подключены —
     * возвращает true сразу.  Иначе пытается инициализировать TronWeb (это
     * вызывает попап разблокировки/подключения кошелька) и при неудаче
     * показывает понятное действие.  Используется во входе/регистрации, чтобы
     * клик сам инициировал подключение, даже если авто-коннект на загрузке не
     * прошёл (заблокированный кошелёк).
     */
    ensureConnected = async (): Promise<boolean> => {
        if (this.tronWeb && this.base58Address) {
            return true
        }
        if (!window.tronLink) {
            notification.warning({
                message: 'TronLink not found',
                description:
                    'Install the TronLink extension and refresh the page.',
            })
            return false
        }
        try {
            await this.initTronWeb()
        } catch {
            // Тихо: ниже покажем пользователю понятное уведомление, без шума
            // в консоли (часто это просто заблокированный кошелёк).
        }
        if (!this.base58Address) {
            // -32603 «No active wallet found» = в TronLink нет рабочего
            // аккаунта: либо кошелёк заблокирован, либо ещё не создан/не
            // импортирован.  Подсказываем оба варианта.
            notification.warning({
                message: 'Unlock TronLink',
                description:
                    'Open the TronLink extension and unlock your wallet (or create/import one and select an account), then click again.',
            })
            return false
        }
        return true
    }

    private _getInstanceContract = async (address: string, abi?: any[]) => {
        try {
            const instance = await this.tronWeb?.contract?.().at(address)
            if (abi) {
                instance?.loadAbi(abi)
            }

            return instance
        } catch (e) {
            console.log(e)
        }
    }

    private _sleep = (ms: number): Promise<void> =>
        new Promise(resolve => setTimeout(resolve, ms))

    /** Адрес из инжектированного TronWeb, если кошелёк разблокирован/подключён. */
    private _readAddress = (): string | undefined =>
        window.tronLink?.tronWeb?.defaultAddress?.base58 || undefined

    /** Дождаться, пока расширение впрыснет провайдера (может с задержкой). */
    private _waitForProvider = async (): Promise<boolean> => {
        for (let i = 0; i < 10 && !window.tronLink; i++) {
            await this._sleep(200)
        }
        return !!window.tronLink
    }

    /**
     * Установлен ли TronLink (доступен ли инжектированный провайдер).
     *
     * Расширение и мобильный dapp-браузер впрыскивают `window.tronLink` с
     * задержкой после загрузки страницы.  Ранняя синхронная проверка
     * `TronLink.tronWeb` ложно показывала «Install TronLink» залогиненным
     * пользователям (после перезагрузки in-memory tronWeb сброшен, хотя
     * расширение и сессия живы).  Здесь ждём инъекцию до ~2с и отвечаем по
     * факту наличия провайдера, а не по факту инициализации соединения.
     */
    isInstalled = async (): Promise<boolean> => this._waitForProvider()

    initTronWeb = async () => {
        // 0. Провайдер расширения может появиться с задержкой после загрузки.
        await this._waitForProvider()

        // 1. Уже подключён и разблокирован — адрес доступен сразу, без запроса.
        let address = this._readAddress()

        // 2. Иначе просим доступ и ждём «прогрева» провайдера до ~6 сек.
        //    На macOS/холодном старте TronLink часто отвечает ready:false
        //    («No active wallet found»), а через секунду-две «просыпается» —
        //    поэтому поллим адрес и мягко пере-запрашиваем доступ раз в ~1.5с.
        //    Это и есть «надёжный вход с первого клика».
        if (!address) {
            await this._connect()
            for (let i = 0; i < 20 && !address; i++) {
                await this._sleep(300)
                address = this._readAddress()
                if (!address && i > 0 && i % 5 === 0) {
                    await this._connect()
                }
            }
        }

        const tronWeb = window.tronLink?.tronWeb

        if (!tronWeb || !address) {
            // Адрес не получили (кошелёк заблокирован / не отдаёт аккаунт).
            // Понятное уведомление пользователю покажет ensureConnected.
            throw new Error('TronLink connection was not established')
        }

        this.tronWeb = tronWeb

        // Prod: жёстко требуем mainnet — нельзя случайно работать с
        // тестовым RCC под видом боевого.  Dev: принимаем любую сеть
        // TronLink (mainnet/Nile/Shasta) чтобы не блокировать локальную
        // разработку при отсутствии Nile-TRX.
        if (
            process.env.NODE_ENV === 'production' &&
            this.tronWeb.fullNode.host !== 'https://api.trongrid.io'
        ) {
            notification.warning({
                message: 'Not mainnet',
                description:
                    'Please, change mainnet in TronLink and refresh page',
            })

            throw new Error('TronLink is not on mainnet')
        }

        this.instanceRcc = await this._getInstanceContract(
            RCC_ADDRESS,
            RCC_ABI
        )

        this.base58Address = address
    }

    signMessage = async (nonce: string): Promise<string | undefined> =>
        await this.tronWeb?.trx.sign(nonce)
}

export const TronLink = new TronLinkHelper()
